import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import { Modal } from "antd";
import {
  getCurrentUserId,
  getDraftKey,
  loadDraft,
  removeDraft,
  saveDraft,
} from "../utils/formDraftStorage";
import DraftStatusBanner from "../Components/FormDraft/DraftStatusBanner";

const defaultIsEmpty = (data) => {
  if (data == null) return true;
  if (typeof data !== "object") return !data;
  if (Array.isArray(data)) return data.length === 0;
  return Object.values(data).every((v) => {
    if (v == null || v === "") return true;
    if (Array.isArray(v)) return v.length === 0;
    return false;
  });
};

function formatDraftDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pt-MZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const modalFooterClass = "flex w-full flex-col gap-3 pt-1";
const modalBtn =
  "w-full rounded-lg px-4 py-2.5 text-sm font-medium transition-colors sm:w-auto sm:min-w-[10rem]";

export default function useFormDraft({
  namespace,
  formId,
  data,
  setData,
  meta,
  setMeta,
  enabled = true,
  ready = true,
  isEmpty = defaultIsEmpty,
  notice,
}) {
  const userId = useMemo(() => getCurrentUserId(), []);
  const draftKey = useMemo(
    () => getDraftKey(userId, namespace, formId),
    [userId, namespace, formId],
  );

  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);

  const [hasEdited, setHasEdited] = useState(false);
  const restoreCheckedRef = useRef(false);
  const skipSaveRef = useRef(false);
  const pendingAfterLeaveRef = useRef(null);

  const isDirty = enabled && hasEdited && !isEmpty(data);

  const markEdited = useCallback(() => {
    if (enabled) setHasEdited(true);
  }, [enabled]);

  const persistDraft = useCallback(async () => {
    if (!enabled || !hasEdited || isEmpty(data)) return;
    const saved = await saveDraft(draftKey, { data, meta });
    if (saved?.savedAt) setLastSavedAt(saved.savedAt);
  }, [enabled, hasEdited, data, meta, draftKey, isEmpty]);

  const clearDraft = useCallback(async () => {
    await removeDraft(draftKey);
    setLastSavedAt(null);
    setHasEdited(false);
  }, [draftKey]);

  const discardDraft = useCallback(async () => {
    await clearDraft();
    Modal.info({
      title: "Rascunho descartado",
      content: "O rascunho local foi removido deste dispositivo.",
      okText: "Entendido",
    });
  }, [clearDraft]);

  useEffect(() => {
    restoreCheckedRef.current = false;
  }, [draftKey]);

  const prevEnabledRef = useRef(enabled);
  useEffect(() => {
    if (enabled && !prevEnabledRef.current) {
      restoreCheckedRef.current = false;
    }
    prevEnabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!ready || restoreCheckedRef.current) return;

    let cancelled = false;
    restoreCheckedRef.current = true;

    (async () => {
      const draft = await loadDraft(draftKey);
      if (cancelled || !draft?.data || isEmpty(draft.data)) return;
      setPendingDraft(draft);
      setShowRestoreModal(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, ready, draftKey, isEmpty]);

  useEffect(() => {
    if (!enabled || !hasEdited || skipSaveRef.current) return;
    if (isEmpty(data)) return;

    const timer = setTimeout(() => {
      persistDraft();
    }, 500);

    return () => clearTimeout(timer);
  }, [enabled, hasEdited, data, meta, persistDraft, isEmpty]);

  useEffect(() => {
    if (!enabled || !hasEdited) return;

    const onBeforeUnload = (e) => {
      if (isEmpty(data)) return;
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [enabled, hasEdited, data, isEmpty]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      enabled &&
      hasEdited &&
      !isEmpty(data) &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowLeaveModal(true);
    }
  }, [blocker.state]);

  const handleRestoreContinue = useCallback(() => {
    if (!pendingDraft) {
      setShowRestoreModal(false);
      return;
    }
    skipSaveRef.current = true;
    setData(pendingDraft.data);
    if (pendingDraft.meta != null && setMeta) {
      setMeta(pendingDraft.meta);
    }
    setLastSavedAt(pendingDraft.savedAt);
    setHasEdited(true);
    setShowRestoreModal(false);
    setPendingDraft(null);
    setTimeout(() => {
      skipSaveRef.current = false;
    }, 600);
  }, [pendingDraft, setData, setMeta]);

  const handleRestoreDiscard = useCallback(async () => {
    await removeDraft(draftKey);
    setPendingDraft(null);
    setShowRestoreModal(false);
    setLastSavedAt(null);
  }, [draftKey]);

  const finishLeave = useCallback(() => {
    const after = pendingAfterLeaveRef.current;
    pendingAfterLeaveRef.current = null;
    after?.();
  }, []);

  const handleLeaveSaveAndExit = useCallback(async () => {
    await persistDraft();
    setShowLeaveModal(false);
    setHasEdited(false);
    if (blocker.state === "blocked") blocker.proceed();
    finishLeave();
  }, [persistDraft, blocker, finishLeave]);

  const handleLeaveWithoutSave = useCallback(async () => {
    await removeDraft(draftKey);
    setLastSavedAt(null);
    setShowLeaveModal(false);
    setHasEdited(false);
    if (blocker.state === "blocked") blocker.proceed();
    finishLeave();
  }, [draftKey, blocker, finishLeave]);

  const handleLeaveContinue = useCallback(() => {
    setShowLeaveModal(false);
    pendingAfterLeaveRef.current = null;
    if (blocker.state === "blocked") blocker.reset();
  }, [blocker]);

  const promptLeave = useCallback(
    (onComplete) => {
      if (enabled && hasEdited && !isEmpty(data)) {
        pendingAfterLeaveRef.current = onComplete ?? null;
        setShowLeaveModal(true);
      } else {
        onComplete?.();
      }
    },
    [enabled, hasEdited, data, isEmpty],
  );

  const draftModals = (
    <>
      <Modal
        title="Rascunho encontrado"
        open={showRestoreModal}
        closable={false}
        maskClosable={false}
        width={440}
        footer={
          <div className={`${modalFooterClass} sm:flex-row sm:justify-end`}>
            <button
              type="button"
              onClick={handleRestoreDiscard}
              className={`${modalBtn} border border-gray-200 text-gray-600 hover:bg-gray-50`}
            >
              Começar de novo
            </button>
            <button
              type="button"
              onClick={handleRestoreContinue}
              className={`${modalBtn} bg-emerald-600 text-white hover:bg-emerald-700`}
            >
              Continuar rascunho
            </button>
          </div>
        }
      >
        <p className="text-gray-600">
          Encontrámos um rascunho guardado em{" "}
          <strong>{formatDraftDate(pendingDraft?.savedAt)}</strong>. Este
          conteúdo <strong>não foi submetido</strong> ao servidor.
        </p>
        <p className="mt-2 text-sm text-gray-500">
          Deseja continuar de onde parou ou começar um formulário novo?
        </p>
      </Modal>

      <Modal
        title="Sair do formulário?"
        open={showLeaveModal}
        onCancel={handleLeaveContinue}
        width={440}
        footer={
          <div className={modalFooterClass}>
            <button
              type="button"
              onClick={handleLeaveContinue}
              className={`${modalBtn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
            >
              Continuar a preencher
            </button>
            <button
              type="button"
              onClick={handleLeaveSaveAndExit}
              className={`${modalBtn} bg-emerald-600 text-white hover:bg-emerald-700`}
            >
              Guardar rascunho e sair
            </button>
            <button
              type="button"
              onClick={handleLeaveWithoutSave}
              className={`${modalBtn} border border-red-200 text-red-600 hover:bg-red-50`}
            >
              Sair sem guardar
            </button>
          </div>
        }
      >
        <p className="text-gray-600">
          Tem alterações que ainda não foram submetidas. O rascunho pode ser
          guardado apenas neste dispositivo.
        </p>
      </Modal>
    </>
  );

  const draftBanner = (
    <DraftStatusBanner
      lastSavedAt={lastSavedAt}
      notice={notice}
      onDiscard={discardDraft}
      visible={enabled && !!lastSavedAt}
    />
  );

  return {
    draftKey,
    lastSavedAt,
    markEdited,
    clearDraft,
    discardDraft,
    persistDraft,
    promptLeave,
    draftModals,
    draftBanner,
    isDirty,
  };
}
